import rootLogger from '@/utils/root-logger';
import fetcher from '@/utils/fetcher';
import errors from '@/errors';

const logger = rootLogger.create('vuex-modules/g2s-recording');

const eventCaptureServiceParams = {
  eventVersion: '1.0',
  eventSource: 'unifiedApp',
  eventType: 'featureUse',
  namespace: 'com.logmein.g2s'
};

const mutations = {
  setEmbeddedResource(state, payload) {
    state.embeddedResource = payload.embeddedResource;
  },

  setProduct(state, payload) {
    state.product = payload.product;
  },

  setProductRefKey(state, payload) {
    state.productRefKey = payload.productRefKey;
  },

  setResourceType(state, payload) {
    state.resourceType = payload.resourceType;
  },

  setChannelProfile(state, payload) {
    state.channelProfile = payload.channelProfile;
  },

  setChannelSettings(state, payload) {
    state.channelSettings = payload.channelSettings;
  },

  setEmail(state, payload) {
    state.email = payload.email;
  },

  setRegistration(state, payload) {
    state.registration = payload.registration;
  },

  setVerifyHumanRegistrant(state, payload) {
    state.verifyHumanRegistrant = payload.verifyHumanRegistrant;
  }
};

const actions = {
  async getEmbeddedResource({ rootGetters, commit, dispatch }, payload ) {
    try {
      const operation = 'getEmbeddedResource';
      const { embeddedResourceId } = payload;

      logger.log(operation, { embeddedResourceId });

      try {
        const path = `/${embeddedResourceId}?withUnderlyingResourceInfo=true`;
        const url = rootGetters['g2wEnv/embeddedResourceServiceBaseUrl'] + path;

        const embeddedResource = await fetcher.fetchJSON(url, {}, {operation});
        const { resource, product, resourceType, productRefKey } = embeddedResource;

        commit('setProduct', { product });
        commit('setResourceType', { resourceType});
        commit('setProductRefKey', { productRefKey});
        commit('setEmbeddedResource', { embeddedResource: resource });

        await dispatch('getVerifyHumanRegistrant', {recordingKey: resource.productRefKey});

        return resource;
      } catch (error) {
        if (error.response.status === 404) {
          logger.error('error video not found', {error});
          commit('setError', {error: errors.g2sRecordingUnavailable}, { root: true });
          return null;
        } else {
          throw error;
        }
      }
    } catch (error) {
      logger.error('error getEmbeddedResource', {error});
      commit('setError', {error: errors.g2sRecordingError}, { root: true });
      throw error;
    }
  },

  async getVerifyHumanRegistrant({rootGetters, commit}, payload) {
    const { recordingKey } = payload;
    const operation = 'getVerifyHumanRegistrant';

    let path = `/api/V2/recordings/${recordingKey}?includes=verifyhumanregistrant`;
    const url = rootGetters['g2wEnv/globalUrl'](path);

    const result = await fetcher.fetchJSON(url, {}, {operation});
    commit('setVerifyHumanRegistrant', { verifyHumanRegistrant: result.verifyHumanRegistrant });
    return result;
  },

  async register({ rootState, commit }, payload) {
    const operation = 'registerForG2WRecording';
    const { productReferenceKey, formData: { firstName, lastName, email, humanRegistrantToken, humanVerificationEngine } } = payload;
    logger.log(operation, { payload });

    commit('setEmail', {email});

    const data = {
      product: 'G2W',
      productReferenceKey,
      resourceType: 'OFFLINEWEBINAR',
      firstName,
      lastName,
      email
    };
    if (humanRegistrantToken) {
      data.humanRegistrantToken = humanRegistrantToken;
      data.humanVerificationEngine = humanVerificationEngine;
    }

    const fetchParams = {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(data)
    };

    const path = `/registrations`;
    const url = rootState.g2wEnv.current.registrationServiceBaseUrl + path;
    let registration;

    try {
      registration = await fetcher.fetchJSON(url, fetchParams, { operation, quiet: true });
    } catch (error) {
      registration = 'no response';
      if (error.response) {
        registration = await error.response.json();
        if (error.response.status === 409) {
          logger.log('g2s register duplicate'); // not a problem...
        } else {
          logger.error('g2s register failed', {operation, registration})
          throw error;
        }
      } else {
        logger.error('g2s register failed', {operation, error})
        throw error;
      }
    }

    commit('setRegistration', { registration: {firstName, lastName, email, ...registration}});

    return registration;
  },

  async getRecordingAssets({ commit, rootState }, payload) {
    const baseUrl = rootState.g2wEnv.current.contentServiceUrl;
    const getRecordingUrl = `${baseUrl}/contents/${payload.contentKey}/asset`;
    const fetchParams = {
      headers: new Headers({
        'Content-Type': 'application/json',
        'x-registrantkey': payload.registrantKey
      })
    };
    const location = await fetcher.fetchJSON(getRecordingUrl, fetchParams, { operation: 'getAssetInfo' });
    commit('recording/setContentFile', { contentFile: location.cdnLocation }, {root: true});
    return {
      contentFile: location.cdnLocation
    };
  },

  async registerAndGetAssets({ state, dispatch, commit }, payload = {}) {
    try {
      const registration = await dispatch('register', {
        productReferenceKey: state.embeddedResource.productRefKey,
        formData: payload.formData
      });

      const registrantKey = registration.registrationKey;

      await dispatch('getRecordingAssets', {
        contentKey: state.embeddedResource.id,
        registrantKey
      });

      return {registrantKey, recordingKey: state.embeddedResource.productRefKey };
    } catch (error) {


      commit('setError', {error: errors.g2sRecordingError}, { root: true });
      throw error;
    }
  },

  async getChannelProfile({ rootState, commit }, payload) {
    const baseUrl = rootState.g2wEnv.current.channelServiceUrl;
    const getChannelProfile = `${baseUrl}/channels?query=contentKey:=${payload.contentKey}`;
    const fetchParams = {
      headers: new Headers({
        'Content-Type': 'application/json'
      })
    };
    const channelProfile = await fetcher.fetchJSON(getChannelProfile, fetchParams, { operation: 'getChannelProfile' });
    commit('setChannelProfile', {channelProfile})
  },

  async getChannelSettings({ commit, rootState }, payload) {
    const baseUrl = rootState.g2wEnv.current.channelServiceUrl;
    const getChannelSettings = `${baseUrl}/channels/${payload.channelKey}/settings`;
    const fetchParams = {
      headers: new Headers({
        'Content-Type': 'application/json'
      })
    };
    try {
      const channelSettings = await fetcher.fetchJSON(getChannelSettings, fetchParams, { operation: 'getChannelSettings', quiet: true });
      commit('setChannelSettings', {channelSettings});
    } catch (err) {
      if (err.response && err.response.status === 404) {
        return {isDisclaimerEnabled: false};
      }
      throw err;
    }
  },

  async getDisclaimer({state, dispatch, commit}) {
    if (!this.disclaimerPromise) {
      this.disclaimerPromise = dispatch('retrieveDisclaimer', {state, dispatch, commit});
    }
    return this.disclaimerPromise;
  },

  async retrieveDisclaimer({state, dispatch, commit}) {
    try {
      await dispatch('getChannelProfile', {contentKey: state.embeddedResource.id});
      await dispatch('getChannelSettings', {channelKey: state.channelProfile[0].channelKey});

      return state.channelSettings && state.channelSettings.isDisclaimerEnabled && state.channelSettings.disclaimerText;
    } catch (error) {
      logger.error('error retrieveDisclaimer', {error});
      commit('setError', {error: errors.g2sRecordingError}, { root: true });
      throw error;
    }
  },

  async initialize({dispatch}, payload) {
    await dispatch('getEmbeddedResource', {embeddedResourceId: payload.embeddedResourceId});
    dispatch('getDisclaimer');
  },

  async sendEvent({rootGetters, state, rootState}, payload) {
    const { eventType, eventVersion, eventSource, namespace} = eventCaptureServiceParams;
    const { eventName, eventBody : eventBodyInput = {} } = payload;
    const { channelKey, channelTitle, contentKey } = state.channelProfile[0];

    const url = rootGetters['g2wEnv/eventServiceBaseUrl'];

    const eventBody = {
      ...eventBodyInput,
      channelKey,
      channelName: channelTitle,
      contentKey,
      email: state.email,
      identityKey: state.registration.registrationKey,
      title: state.embeddedResource.title,
      url: rootState.recording.contentFile
    }

    const event = {
      eventName,
      eventBody,
      eventType,
      eventVersion,
      eventTime: Date.now(),
      eventSource,
      namespace
    };

    logger.log('sendEvent', event);

    const fetchParams = {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(event)
    };

    try {
      return await fetcher.fetchJSON(url, fetchParams, { operation: 'sendEvent' });
    } catch (err) {
      logger.warn('error sending event to event capture service', err);
    }
  }
};

const getters = {
  productInfo(state) {
    if (/g2s/i.test(state.product)) {
      return {
        logoSrc: require('@/assets/g2s-logo.svg'),
        href: 'https://www.gotostage.com',
      };
    }
    return null;
  }
};

const state = () => ({
  embeddedResource: null,
  channelProfile: null,
  channelSettings: null,
  email: null,
  product: null,
  registration: null,
  verifyHumanRegistrant: false
});

export default {
  namespaced: true,
  getters,
  state,
  mutations,
  actions
};
